import React from 'react';
import { lbl, inp } from './shared';

const PRODUCTS = [
  {
    id: 'partD',
    label: 'Stand-alone Medicare Prescription Drug Plans (Part D)',
    summary: 'Adds prescription drug coverage to Original Medicare.',
    details: [
      { name: 'Medicare Prescription Drug Plan (PDP)', text: 'A stand-alone drug plan that adds prescription drug coverage to Original Medicare, some Medicare Cost Plans, some Medicare Private-Fee-for-Service Plans, and Medicare Medical Savings Account Plans.' },
    ],
  },
  {
    id: 'partC',
    label: 'Medicare Advantage Plans (Part C) and Cost Plans',
    summary: 'All-in-one Medicare coverage replacing Original Medicare — includes HMO, PPO, and other plan types.',
    details: [
      { name: 'HMO', text: 'Provides all Original Medicare Part A and Part B health coverage and sometimes covers Part D. In most HMOs, you can only get care from doctors or hospitals in the plan\'s network (except in emergencies).' },
      { name: 'PPO', text: 'Provides all Original Medicare Part A and Part B health coverage and sometimes covers Part D. PPOs have network doctors and hospitals but you can also use out-of-network providers, usually at a higher cost.' },
      { name: 'PFFS (Private Fee-For-Service)', text: 'You may go to any Medicare-approved doctor, hospital, and provider that accepts the plan\'s payment terms and conditions — not all providers will. You will usually pay more to see out-of-network providers.' },
      { name: 'POS (Point of Service)', text: 'Combines HMO features with an out-of-network benefit. Members designate an in-network physician as their primary care provider but can use out-of-network providers at an additional cost.' },
      { name: 'SNP (Special Needs Plan)', text: 'Designed for people with special health care needs, including those with both Medicare and Medicaid, people in nursing homes, or those with certain chronic medical conditions.' },
      { name: 'MSA (Medical Savings Account)', text: 'Combines a high-deductible health plan with a bank account. The plan deposits money from Medicare into the account, which you can use to pay medical expenses until your deductible is met.' },
      { name: 'Medicare Cost Plan', text: 'You can go to providers both in and out of network. Services outside the plan\'s network are covered under Original Medicare, but you are responsible for Medicare coinsurance and deductibles.' },
      { name: 'MMP (Medicare-Medicaid Plan)', text: 'A private health plan providing integrated and coordinated Medicare and Medicaid benefits for dual-eligible Medicare beneficiaries.' },
    ],
  },
  {
    id: 'dental',
    label: 'Dental / Vision / Hearing Products',
    summary: 'Additional benefits for dental, vision, or hearing needs.',
    details: [
      { name: null, text: 'Plans offering additional benefits for consumers looking to cover needs for dental, vision, or hearing. These plans are not affiliated or connected to Medicare.' },
    ],
  },
  {
    id: 'hospital',
    label: 'Hospital Indemnity Products',
    summary: 'Supplemental plans that pay benefits based on medical utilization to help offset copays and coinsurance.',
    details: [
      { name: null, text: 'Plans offering additional benefits payable to consumers based upon their medical utilization; sometimes used to defray copays and coinsurance. These plans are not affiliated or connected to Medicare.' },
    ],
  },
  {
    id: 'supplement',
    label: 'Medicare Supplement (Medigap) Products',
    summary: 'Fills gaps in Original Medicare — deductibles, coinsurance, and some non-covered services.',
    details: [
      { name: null, text: 'Plans offering a supplemental policy to fill "gaps" in Original Medicare coverage. A Medigap policy typically pays some or all of the deductible and coinsurance amounts applicable to Medicare-covered services, and sometimes covers items and services not covered by Medicare, such as care outside of the country. These plans are not affiliated or connected to Medicare.' },
    ],
  },
];

export default function ScopeOfAppointment({ data, onChange }) {
  const today = new Date().toLocaleDateString('en-US');
  const products = data.soaProducts || [];
  const timestamps = data.soaProductTimestamps || {};

  function toggleProduct(id) {
    const isChecked = products.includes(id);
    const next = isChecked ? products.filter(p => p !== id) : [...products, id];
    onChange('soaProducts', next);
    onChange('soaProductTimestamps', {
      ...timestamps,
      [id]: isChecked ? null : new Date().toISOString(),
    });
  }

  function handleConsentChange(checked) {
    onChange('soaAgreedToElectronicSignature', checked);
    onChange('soaConsentTimestamp', checked ? new Date().toISOString() : null);
  }

  return (
    <div>
      <p style={{ fontSize: 13, color: '#4a6288', lineHeight: 1.6, marginTop: 0, marginBottom: 20 }}>
        The Centers for Medicare and Medicaid Services requires agents to document the scope of a marketing
        appointment prior to any individual sales meeting. All information is confidential and should be completed
        by the Medicare beneficiary or their authorized representative.
      </p>

      {/* Product selection */}
      <div style={{ marginBottom: 24 }}>
        <div style={{
          background: '#1a3364', color: '#fff',
          borderRadius: 8, padding: '12px 16px', marginBottom: 14,
        }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 3 }}>
            Select the product(s) you want the agent to discuss
          </div>
          <div style={{ fontSize: 12, color: '#b8cfe8', fontWeight: 400 }}>
            Check all that apply — then tap "View product descriptions" to learn more about each type.
          </div>
        </div>
        {PRODUCTS.map(p => {
          const selected = products.includes(p.id);
          return (
            <div key={p.id} style={{ marginBottom: 8 }}>
              <div
                onClick={() => toggleProduct(p.id)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 14,
                  padding: '12px 16px',
                  border: `2px solid ${selected ? '#1a3364' : '#c5d5e8'}`,
                  borderRadius: selected ? '8px 8px 0 0' : 8,
                  background: selected ? '#eef4fb' : '#fafcfe',
                  cursor: 'pointer', transition: 'border-color 0.15s, background 0.15s',
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: 4, flexShrink: 0, marginTop: 1,
                  border: `2px solid ${selected ? '#1a3364' : '#b8cfe8'}`,
                  background: selected ? '#1a3364' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {selected && <span style={{ color: '#fff', fontSize: 13, fontWeight: 700 }}>✓</span>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1a3364' }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: '#7a99b8', marginTop: 3, lineHeight: 1.4 }}>{p.summary}</div>
                </div>
              </div>

              <details style={{
                border: `2px solid ${selected ? '#1a3364' : '#c5d5e8'}`, borderTop: 'none',
                borderRadius: '0 0 8px 8px', background: '#f8fbff',
              }}>
                <summary style={{
                  padding: '8px 16px', fontSize: 12, color: '#2a5298',
                  fontWeight: 600, cursor: 'pointer', userSelect: 'none',
                  listStyle: 'none', display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  ▸ View product descriptions
                </summary>
                <div style={{ padding: '4px 16px 14px' }}>
                  {p.details.map((d, i) => (
                    <div key={i} style={{ marginTop: 10 }}>
                      {d.name && (
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1a3364', marginBottom: 3 }}>
                          {d.name}
                        </div>
                      )}
                      <div style={{ fontSize: 12, color: '#4a6288', lineHeight: 1.5 }}>{d.text}</div>
                    </div>
                  ))}
                </div>
              </details>
            </div>
          );
        })}
      </div>

      {/* Authorized representative */}
      <div style={{ marginBottom: 18 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={!!data.soaIsAuthorizedRep}
            onChange={e => onChange('soaIsAuthorizedRep', e.target.checked)}
            style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#1a3364' }}
          />
          <span style={{ fontSize: 14, color: '#1a3364', fontWeight: 500 }}>
            I am signing as an authorized representative (not the beneficiary)
          </span>
        </label>
      </div>

      {data.soaIsAuthorizedRep && (
        <div style={{
          background: '#f4f8fc', border: '1.5px solid #c5d5e8',
          borderRadius: 10, padding: '16px 18px', marginBottom: 20,
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={lbl}>Representative's Name</label>
              <input style={inp} value={data.soaRepName || ''} onChange={e => onChange('soaRepName', e.target.value)} />
            </div>
            <div>
              <label style={lbl}>Relationship to Beneficiary</label>
              <input style={inp} value={data.soaRepRelationship || ''} onChange={e => onChange('soaRepRelationship', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {/* Legal consent + signature */}
      <div style={{
        background: '#fff8e8', border: '2px solid #d89b2b',
        borderRadius: 10, padding: '18px 20px', marginBottom: 20,
        boxShadow: '0 8px 24px rgba(26, 51, 100, 0.08)',
      }}>
        <p style={{ fontSize: 13, color: '#4a6288', lineHeight: 1.6, margin: '0 0 16px' }}>
          By signing below, you agree to a meeting with a sales agent to discuss the product types selected above.
          The agent is employed or contracted by a Medicare plan and may be paid based on your enrollment.{' '}
          <strong style={{ color: '#1a3364' }}>
            Signing does NOT obligate you to enroll in a plan or affect your current or future Medicare enrollment.
          </strong>
        </p>

        <div style={{ fontSize: 13, fontWeight: 800, color: '#1a3364', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>
          Electronic Signature Agreement
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', userSelect: 'none', marginBottom: 18 }}>
          <input
            type="checkbox"
            checked={!!data.soaAgreedToElectronicSignature}
            onChange={e => handleConsentChange(e.target.checked)}
            style={{ width: 22, height: 22, cursor: 'pointer', accentColor: '#1a3364', marginTop: 0, flex: '0 0 auto' }}
          />
          <span style={{ fontSize: 15, color: '#1a3364', fontWeight: 800, lineHeight: 1.45 }}>
            I agree to use an electronic signature and consent to the terms above.
          </span>
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
          <div>
            <label style={lbl}>Type Your Full Legal Name as Signature</label>
            <input
              style={{ ...inp, fontStyle: 'italic', fontSize: 16, letterSpacing: '0.02em' }}
              value={data.soaSignature || ''}
              onChange={e => onChange('soaSignature', e.target.value)}
              placeholder="Full legal name"
              disabled={!data.soaAgreedToElectronicSignature}
            />
          </div>
          <div>
            <label style={lbl}>Signature Date</label>
            <input
              style={{ ...inp, background: '#e8f0f8', color: '#4a6288', cursor: 'not-allowed' }}
              value={today}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
}
